import React from 'react';
import { compose, withStateHandlers, withHandlers } from 'recompose';
import validator from 'validator';
import './DomainsField.scss';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../../common/components/TextInput';
import {
  ENTER_CODE,
  TAB_CODE,
} from "@mm/constants/keys-codes";
import { TagItem } from '../../../../../common/components/TagItem/TagItem';
import domainMessages from '../../constants/domainMessages';

const initialState = {
  domain: '',
  error: '',
  tip: domainMessages.tip,
};

const stateUpdaters = {
  setDomain: () => domain => ({ domain }),
  setError: () => error => ({ error }),
};

export const normalizeDomain = () => domain => {
  let normalizedDomain = domain;
  if (!normalizedDomain.startsWith('http')) {
    normalizedDomain = `http://${normalizedDomain}`;
  }

  if (!normalizedDomain.endsWith('/')) {
    normalizedDomain = `${normalizedDomain}/`;
  }

  return normalizedDomain;
};

export const validateDomain = ({ normalizeDomain, domains }) => value => {
  let error = '';

  if (
    !validator.isURL(value, {
      require_protocol: false,
      protocols: ['http', 'https'],
    })
  ) {
    error = domainMessages.incorrect_error;

    return error;
  }

  const normalizedDomain = normalizeDomain(value);
  if (domains.includes(normalizedDomain)) {
    error = domainMessages.domain_exists;

    return error;
  }

  return error;
};

const proccessValidation = ({ domain, validateDomain, setError }) => () => {
  const validationResult = validateDomain(domain);

  if (validationResult) {
    setError(validationResult);
    return false;
  }
  setError('');

  return true;
};

const onChangeDomain = ({ setDomain }) => e => setDomain(e.target.value);

const onAddDomain = props => () => {
  const {
    proccessValidation,
    normalizeDomain,
    domain,
    domains,
    updateDomains,
  } = props;

  const isDomainValid = proccessValidation();

  if (!isDomainValid) {
    return false;
  }

  const normalizedDomain = normalizeDomain(domain);
  updateDomains([...domains, normalizedDomain]);

  return true;
};

const onTriggerAddDomain = ({ domain, setDomain, onAddDomain }) => () => {
  const haveAdded = onAddDomain(domain);

  if (haveAdded) {
    setDomain('');
  }
};

const onKeyDownDomain = ({ onTriggerAddDomain }) => e => {
  if (e.keyCode === ENTER_CODE || e.keyCode === TAB_CODE) {
    e.preventDefault();
    onTriggerAddDomain();
  }
};

const onDeleteDomain = ({ domains, updateDomains }) => value => {
  const domainIndex = domains.findIndex(domain => domain === value);
  updateDomains([
    ...domains.slice(0, domainIndex),
    ...domains.slice(domainIndex + 1),
  ]);
};

const renderDomainsList = ({ domains, onDeleteDomain }) => () =>
  domains.map(domain => (
    <div key={domain} className="domains-field__domain-item">
      <TagItem text={domain} onDelete={() => onDeleteDomain(domain)} />
    </div>
  ));

export const enhance = compose(
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ normalizeDomain, onChangeDomain, onDeleteDomain }),
  withHandlers({ validateDomain, renderDomainsList }),
  withHandlers({ proccessValidation }),
  withHandlers({ onAddDomain }),
  withHandlers({ onTriggerAddDomain }),
  withHandlers({ onKeyDownDomain }),
);

export const DomainsFieldComponent = props => {
  const {
    domain,
    error,
    onChangeDomain,
    onKeyDownDomain,
    onTriggerAddDomain,
    renderDomainsList,
    tip,
  } = props;

  const domainsList = renderDomainsList();

  return (
    <div className="domains-field">
      <div className="domains-field__header">Add Domain to Whitelist</div>
      <div className="domains-field__content">
        <div className="domains-field__tip">{tip}</div>
        <div className="domains-field__info">
          {error && (
            <span className="domains-field__error-message">{error}</span>
          )}
          <div className="domains-field__input-container">
            <TextInput
              value={domain}
              onChange={onChangeDomain}
              onKeyDown={onKeyDownDomain}
              className="domains-field__domain-input"
            />

            <PrimaryButton onClick={onTriggerAddDomain}>Add</PrimaryButton>
          </div>
          <div className="domains-field__domains-cloud">{domainsList}</div>
        </div>
      </div>
    </div>
  );
};

export default enhance(DomainsFieldComponent);
